import { compressImageFile } from '../../../utils/imageCompress';
import { useRef } from 'react';
import { Button, Input, InputNumber, Segmented, Select, Typography, message } from 'antd';
import { PlusOutlined, DeleteOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import type { FieldMeta } from '../../../api/types';
import type { SignatureSection, SignatureParty } from '../../../contract/types';
import { genId } from '../../../contract/types';
import { defaultParty } from '../../../contract/signature';

interface Props {
  section: SignatureSection;
  onChange: (s: SignatureSection) => void;
  fieldMetas: FieldMeta[];
}

/** 印章图上传上限：模板存 localStorage，太大会撑爆存储。 */
const SEAL_MAX_BYTES = 1024 * 1024;

export function SignatureEditor({ section, onChange, fieldMetas }: Props) {
  const fieldOptions = fieldMetas.map((m) => ({ label: m.name, value: m.id }));
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const uploadTargetRef = useRef<string | null>(null);

  const updateParty = (id: string, patch: Partial<SignatureParty>) => {
    const parties = section.parties.map((p) => (p.id === id ? { ...p, ...patch } : p));
    onChange({ ...section, parties });
  };

  const pickSeal = (partyId: string) => {
    uploadTargetRef.current = partyId;
    fileInputRef.current?.click();
  };

  const onSealFile = (file: File | undefined) => {
    const partyId = uploadTargetRef.current;
    if (!file || !partyId) return;
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) {
      message.error('请上传 PNG/JPG 图片（推荐透明底 PNG 公章图）');
      return;
    }
    if (file.size > SEAL_MAX_BYTES) {
      message.error('印章图过大（>1MB），请压缩后再上传');
      return;
    }
    void compressImageFile(file).then((dataUrl) => {
      updateParty(partyId, { sealDataUrl: dataUrl });
      message.success('印章已设置，预览里可见盖章效果');
    });
  };

  const addParty = () => {
    const label = `${'甲乙丙丁戊'[section.parties.length] ?? '第' + (section.parties.length + 1)}方（盖章）`;
    onChange({ ...section, parties: [...section.parties, defaultParty(label)] });
  };

  const removeParty = (id: string) => {
    onChange({ ...section, parties: section.parties.filter((p) => p.id !== id) });
  };

  const updateRow = (partyId: string, rowId: string, patch: { label?: string; fieldId?: string | null; staticValue?: string }) => {
    const parties = section.parties.map((p) => {
      if (p.id !== partyId) return p;
      return {
        ...p,
        rows: p.rows.map((r) => (r.id === rowId ? { ...r, ...patch } : r)),
      };
    });
    onChange({ ...section, parties });
  };

  const addRow = (partyId: string) => {
    const parties = section.parties.map((p) => {
      if (p.id !== partyId) return p;
      return { ...p, rows: [...p.rows, { id: genId(), label: '', fieldId: null, staticValue: '' }] };
    });
    onChange({ ...section, parties });
  };

  const removeRow = (partyId: string, rowId: string) => {
    const parties = section.parties.map((p) => {
      if (p.id !== partyId) return p;
      return { ...p, rows: p.rows.filter((r) => r.id !== rowId) };
    });
    onChange({ ...section, parties });
  };

  return (
    <div>
      {section.parties.length === 1 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>落款位置</Typography.Text>
          <Segmented
            size="small"
            value={section.align === 'right' ? 'right' : 'left'}
            options={[
              { label: '靠左', value: 'left' },
              { label: '靠右', value: 'right' },
            ]}
            onChange={(v) => onChange({ ...section, align: v === 'right' ? 'right' : undefined })}
          />
        </div>
      )}
      {section.parties.map((party) => (
        <div
          key={party.id}
          style={{ border: '1px solid #e8e8e8', borderRadius: 4, padding: 8, marginBottom: 8 }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <Input
              size="small"
              style={{ width: 160 }}
              value={party.label}
              onChange={(e) => updateParty(party.id, { label: e.target.value })}
              placeholder="甲方（盖章）"
            />
            <Button
              size="small"
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => removeParty(party.id)}
            />
          </div>
          {party.rows.map((row) => (
            <div key={row.id} style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 3 }}>
              <Input
                size="small"
                style={{ width: 100 }}
                value={row.label}
                onChange={(e) => updateRow(party.id, row.id, { label: e.target.value })}
                placeholder="标签"
              />
              <Select
                size="small"
                style={{ flex: 1, minWidth: 0 }}
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="绑定字段"
                value={row.fieldId}
                onChange={(v) => updateRow(party.id, row.id, { fieldId: v ?? null })}
                options={fieldOptions}
              />
              {!row.fieldId && (
                <Input
                  size="small"
                  style={{ width: 70 }}
                  value={row.staticValue}
                  onChange={(e) => updateRow(party.id, row.id, { staticValue: e.target.value })}
                  placeholder="静态值"
                />
              )}
              <Button
                size="small"
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeRow(party.id, row.id)}
              />
            </div>
          ))}
          <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => addRow(party.id)}>
            添加行
          </Button>

          {/* 盖章：上传公章图，渲染时按实务尺寸压在本方签字栏上 */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
            {party.sealDataUrl ? (
              <>
                <img
                  src={party.sealDataUrl}
                  alt="印章"
                  style={{ width: 34, height: 34, objectFit: 'contain', border: '1px solid #eee', borderRadius: 4 }}
                />
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>章直径(mm)</Typography.Text>
                <InputNumber
                  size="small"
                  min={15}
                  max={80}
                  style={{ width: 64 }}
                  value={party.sealSizeMm ?? 40}
                  onChange={(v) => updateParty(party.id, { sealSizeMm: v ?? 40 })}
                />
                <Button size="small" onClick={() => pickSeal(party.id)}>
                  更换
                </Button>
                <Button
                  size="small"
                  type="text"
                  danger
                  onClick={() => updateParty(party.id, { sealDataUrl: null })}
                >
                  去章
                </Button>
              </>
            ) : (
              <>
                <Button
                  size="small"
                  icon={<SafetyCertificateOutlined />}
                  onClick={() => pickSeal(party.id)}
                >
                  上传印章
                </Button>
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  透明底 PNG 最佳，默认 40mm 实务尺寸
                </Typography.Text>
              </>
            )}
          </div>
        </div>
      ))}
      <Button size="small" icon={<PlusOutlined />} onClick={addParty}>
        添加签署方
      </Button>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => {
          onSealFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </div>
  );
}
